import type { MouseEvent } from 'react'
import type {
  IndexCompetition,
  IndexHomeData,
  IndexGroup,
  IndexGroupScope,
  IndexItem,
  IndexKind,
  IndexResults,
  IndexScope,
} from './types'

/**
 * What the Index body shows. One decision, made here, so every state is a
 * row in a table rather than a branch buried in JSX:
 *
 * - home     nothing typed — live competitions, open registrations, recents
 * - empty    typed, nothing matched
 * - list     one tab picked — that kind, with a preview
 * - focused  one or two kinds matched — top hit, a grouped list, a preview
 * - mixed    three or more kinds — top hit, a blended top five, a shelf per kind
 */
export type IndexLayout = 'home' | 'empty' | 'list' | 'focused' | 'mixed'

export interface IndexSection {
  /** Stable id for keys and aria. */
  id: string
  label: string
  /** Matches behind this section; more than items when there is a next page. */
  total: number
  /** The tab that shows all of them, for a "See all" link. */
  scope?: IndexGroupScope
  items: IndexItem[]
  /** More matches exist than this response carried (the top hit counts as shown). */
  hasMore: boolean
}

export interface IndexView {
  layout: IndexLayout
  topHit: IndexItem | null
  /** Mixed layout: the best of each other kind, next to the top hit. */
  topResults: IndexItem[]
  sections: IndexSection[]
  /** Every rendered result in reading order — what the arrow keys walk. */
  order: { key: string; item: IndexItem }[]
}

export const GROUP_LABELS: Record<IndexGroupScope, string> = {
  competitions: 'Competitions',
  leagues: 'Leagues',
  clubs: 'Clubs',
  teams: 'Teams',
  players: 'Players',
}

export const SCOPE_TABS: { scope: IndexScope; label: string }[] = [
  { scope: 'all', label: 'All' },
  { scope: 'players', label: 'Players' },
  { scope: 'teams', label: 'Teams' },
  { scope: 'clubs', label: 'Clubs' },
  { scope: 'leagues', label: 'Leagues' },
  { scope: 'competitions', label: 'Competitions' },
]

export const KIND_LABELS: Record<IndexKind, string> = {
  player: 'Player',
  team: 'Team',
  club: 'Club',
  university: 'University',
  league: 'League',
  competition: 'Competition',
}

const TOP_RESULTS = 4

export function itemKey(section: string, item: IndexItem): string {
  return `${section}:${item.kind}:${item.id}`
}

/** The same result can appear twice (top hit and its shelf); identity ignores where. */
function sameItem(a: IndexItem, b: IndexItem): boolean {
  return a.kind === b.kind && a.id === b.id
}

/**
 * The blended row beside the top hit: the best result of every other kind
 * first, so one keyword visibly reaches across the platform, then the next
 * best of anything to fill it.
 */
export function blendTopResults(groups: IndexGroup[], topHit: IndexItem | null, limit = TOP_RESULTS): IndexItem[] {
  const pool = groups
    .flatMap((g) => g.items)
    .filter((it) => !(topHit && sameItem(it, topHit)))
    // A club reached through its team is context, not a top result.
    .filter((it) => !('relatedVia' in it && it.relatedVia))
    .sort((a, b) => b.score - a.score)

  const picked: IndexItem[] = []
  const seenKinds = new Set<string>(topHit ? [topHit.kind] : [])
  for (const it of pool) {
    if (picked.length === limit) break
    if (!seenKinds.has(it.kind)) {
      picked.push(it)
      seenKinds.add(it.kind)
    }
  }
  for (const it of pool) {
    if (picked.length === limit) break
    if (!picked.includes(it)) picked.push(it)
  }
  return picked.sort((a, b) => b.score - a.score)
}

export function buildView(results: IndexResults | null, scope: IndexScope): IndexView {
  const none: IndexView = { layout: 'home', topHit: null, topResults: [], sections: [], order: [] }
  if (!results || !results.query) return none

  const groups = results.groups.filter((g) => g.items.length > 0)
  if (groups.length === 0) return { ...none, layout: 'empty' }

  const toSection = (g: IndexGroup, items = g.items): IndexSection => ({
    id: g.scope,
    label: GROUP_LABELS[g.scope],
    total: g.total,
    scope: g.scope,
    items,
    hasMore: g.total > g.items.length,
  })

  if (scope !== 'all') {
    const sections = groups.map((g) => toSection(g))
    return { layout: 'list', topHit: null, topResults: [], sections, order: orderOf(null, [], sections) }
  }

  const topHit = results.topHit
  if (groups.length <= 2) {
    // The top hit is shown on its own, so its group doesn't repeat it.
    const sections = groups
      .map((g) => toSection(g, g.items.filter((it) => !(topHit && sameItem(it, topHit)))))
      .filter((s) => s.items.length > 0)
    return { layout: 'focused', topHit, topResults: [], sections, order: orderOf(topHit, [], sections) }
  }

  const topResults = blendTopResults(groups, topHit)
  const sections = groups.map((g) => toSection(g))
  return { layout: 'mixed', topHit, topResults, sections, order: orderOf(topHit, topResults, sections) }
}

function orderOf(topHit: IndexItem | null, topResults: IndexItem[], sections: IndexSection[]) {
  const order: { key: string; item: IndexItem }[] = []
  if (topHit) order.push({ key: itemKey('top', topHit), item: topHit })
  for (const it of topResults) order.push({ key: itemKey('blend', it), item: it })
  for (const s of sections) for (const it of s.items) order.push({ key: itemKey(s.id, it), item: it })
  return order
}

/** Totals per tab, for the counts on the scope tabs. */
export function scopeTotals(results: IndexResults | null): Partial<Record<IndexScope, number>> {
  if (!results || !results.query) return {}
  const totals: Partial<Record<IndexScope, number>> = {}
  let all = 0
  for (const g of results.groups) {
    totals[g.scope] = g.total
    all += g.total
  }
  totals.all = all
  return totals
}

// --- Competitions -------------------------------------------------------------

const DAY_MS = 24 * 60 * 60 * 1000

const FORMAT_LABELS: Record<string, string> = {
  round_robin: 'Round robin',
  swiss: 'Swiss',
  single_elimination: 'Single elim',
  double_elimination: 'Double elim',
}

export function formatLabel(format: string): string {
  if (FORMAT_LABELS[format]) return FORMAT_LABELS[format]
  const words = format.replace(/_/g, ' ').trim()
  return words ? words[0].toUpperCase() + words.slice(1) : ''
}

export type CompetitionTone = 'live' | 'active' | 'open' | 'muted'

export interface CompetitionStatus {
  label: string
  tone: CompetitionTone
}

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

/**
 * Where a competition stands, said the way a player asks it: can I watch it,
 * can I still get in, when does it start. "Active" alone answers none of those.
 */
export function competitionStatus(c: IndexCompetition, now: number = Date.now()): CompetitionStatus {
  switch (c.phase) {
    case 'live':
      return { label: 'Live now', tone: 'live' }
    case 'playoffs':
      return { label: 'Playoffs', tone: 'active' }
    case 'in_progress': {
      if (c.competitionType === 'league' && c.startsAt) {
        const elapsed = now - new Date(c.startsAt).getTime()
        if (elapsed >= 0) return { label: `Week ${Math.floor(elapsed / (7 * DAY_MS)) + 1}`, tone: 'active' }
      }
      return { label: 'In progress', tone: 'active' }
    }
    case 'registration_open': {
      if (c.registrationClosesAt) {
        const left = new Date(c.registrationClosesAt).getTime() - now
        if (left > 0 && left < 7 * DAY_MS) {
          const days = Math.floor(left / DAY_MS)
          return { label: days < 1 ? 'Closes today' : `Closes in ${days}d`, tone: 'open' }
        }
      }
      return { label: 'Registration open', tone: 'open' }
    }
    case 'upcoming':
      return { label: c.startsAt ? `Starts ${shortDate(c.startsAt)}` : 'Starting soon', tone: 'muted' }
    case 'completed':
      return { label: 'Finished', tone: 'muted' }
  }
}

/** "8 teams", "8 / 16 teams" — entries against the cap when there is one. */
export function teamsLabel(c: IndexCompetition): string {
  const noun = (n: number) => (n === 1 ? 'team' : 'teams')
  if (c.maxTeams) return `${c.registeredTeams} / ${c.maxTeams} ${noun(c.maxTeams)}`
  return `${c.registeredTeams} ${noun(c.registeredTeams)}`
}

export function prizeLabel(cents: number): string {
  return `$${Math.round(cents / 100).toLocaleString()}`
}

// --- Tiles ----------------------------------------------------------------------

/**
 * Black or white letters for a lettered tile, by the colour's luminance — a
 * pale brand colour (Thunder Hawks' yellow) needs dark text to be readable.
 * Anything that isn't a #rgb / #rrggbb hex keeps white.
 */
export function tileTextColor(color: string | undefined): 'light' | 'dark' {
  const hex = color?.trim().replace(/^#/, '') ?? ''
  if (!/^([0-9a-f]{3}|[0-9a-f]{6})$/i.test(hex)) return 'light'
  const full = hex.length === 3 ? hex.split('').map((ch) => ch + ch).join('') : hex
  const channel = (i: number) => {
    const v = parseInt(full.slice(i, i + 2), 16) / 255
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4
  }
  const luminance = 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4)
  // White on the colour vs. near-black on it: pick whichever contrasts more.
  const onWhite = 1.05 / (luminance + 0.05)
  const onDark = (luminance + 0.05) / 0.05
  return onDark > onWhite ? 'dark' : 'light'
}

/**
 * Splits a name around the first case-insensitive occurrence of the keyword,
 * so the matched letters can be highlighted. No match, no highlight.
 */
export function splitMatch(name: string, query: string): { before: string; match: string; after: string } {
  const q = query.trim().toLowerCase()
  const at = q ? name.toLowerCase().indexOf(q) : -1
  if (at < 0) return { before: name, match: '', after: '' }
  return { before: name.slice(0, at), match: name.slice(at, at + q.length), after: name.slice(at + q.length) }
}

// --- Rows ---------------------------------------------------------------------

/** A competition wears its league's tile — that is whose logo it has. */
export function tileName(item: IndexItem): string {
  return item.kind === 'competition' ? item.orgName : item.name
}

function plural(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? '' : 's'}`
}

/** The second line of a result: who it belongs to, what it plays. */
export function itemSubtitle(item: IndexItem): string {
  switch (item.kind) {
    case 'player': {
      const handle = `@${item.handle}`
      if (!item.teamName) return handle
      const role = item.teamRole ? `${item.teamRole[0].toUpperCase()}${item.teamRole.slice(1)}, ` : ''
      return `${handle} · ${role}${item.teamName}`
    }
    case 'team':
      return `${item.orgName} · ${item.gameName}`
    case 'competition':
      return [item.orgName, item.gameName, formatLabel(item.format)].filter(Boolean).join(' · ')
    default: {
      if (item.relatedVia) return `${KIND_LABELS[item.kind]} · home of ${item.relatedVia}`
      const parts = [KIND_LABELS[item.kind]]
      if (item.teamCount) parts.push(plural(item.teamCount, 'team'))
      if (item.competitionCount) parts.push(plural(item.competitionCount, 'competition'))
      return parts.join(' · ')
    }
  }
}

/**
 * Plain left-clicks go through onNavigate so the app can route and close the
 * Index; a modified click (new tab, new window) is left to the browser.
 */
export function followLink(e: MouseEvent, href: string, onNavigate: (href: string) => void) {
  if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
  e.preventDefault()
  onNavigate(href)
}

/** The home sections' results in reading order, for the arrow keys. */
export function homeOrder(home: IndexHomeData): { key: string; item: IndexCompetition }[] {
  return [
    ...home.live.map((item) => ({ key: itemKey('live', item), item })),
    ...home.open.map((item) => ({ key: itemKey('open', item), item })),
  ]
}

